import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  Badge,
  Tooltip,
  ListItemSecondaryAction,
  ListItemIcon
} from '@mui/material';
import {
  Close as CloseIcon,
  NotificationsActive as NotificationsActiveIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import systemAPI from '../../utils/systemAPI';
import { formatDistanceToNow } from 'date-fns';

const SystemNotifications = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications when drawer opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  // Function to fetch notifications from API
  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await systemAPI.getNotifications();
      if (response && response.data) {
        setNotifications(response.data);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await systemAPI.markNotificationsAsRead();
      // Update local state to show notifications as read
      setNotifications(notifications.map(notif => ({ ...notif, read: true })));
    } catch (err) {
      console.error('Error marking notifications as read:', err);
      setError('Failed to mark notifications as read');
    }
  };

  // Delete a notification
  const deleteNotification = async (id) => {
    try {
      await systemAPI.deleteNotification(id);
      // Remove the deleted notification from state
      setNotifications(notifications.filter(notif => notif._id !== id));
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  // Get the appropriate icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      elevation={3}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <NotificationsActiveIcon sx={{ mr: 1 }} />
          System Notifications
        </Typography>
        <IconButton onClick={onClose} edge="end" aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <Button 
          size="small" 
          onClick={markAllAsRead}
          startIcon={<CheckIcon />}
          disabled={loading || notifications.every(n => n.read) || notifications.length === 0}
        >
          Mark all as read
        </Button>
      </Box>
      
      <Divider />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography>Loading notifications...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="outlined" 
            onClick={fetchNotifications} 
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No notifications</Typography>
        </Box>
      ) : (
        <List sx={{ pt: 0, pb: 2 }}>
          {notifications.map((notification) => (
            <React.Fragment key={notification._id}>
              <ListItem 
                alignItems="flex-start"
                sx={{ 
                  bgcolor: notification.read ? 'transparent' : 'action.hover',
                  pr: 6, // Make room for the delete button
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography component="span" variant="subtitle2" sx={{ mr: 1 }}>
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Badge color="error" variant="dot" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {notification.createdAt && 
                          formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Delete notification">
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={() => deleteNotification(notification._id)}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Drawer>
  );
};

export default SystemNotifications;
